import { useState, memo, lazy } from 'react';

import { useAuthentication } from '@zenbusiness/application-commons-authentication';
import { saveAs } from 'file-saver';
import PropTypes from 'prop-types';
import { generatePath } from 'react-router-dom';

import Document from '#client/components/StatementsModal/components/Document/Document';
import { fetchPDF } from '#client/components/StatementsModal/utils/pdf';
import { MODAL_NAMES } from '#client/constants/modals';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import useModalContext from '#client/hooks/useModalContext';
import useToastContext from '#client/hooks/useToastContext';
import { DEFAULT_SEGMENT } from '#client/manifest';
import { checkNotAllowedBrowsersForPDF } from '#client/utils/browserVersion';
import { convertMonthToNumber } from '#client/utils/statements';

import '#client/components/ViewPdfModal/utils/polyfill';

const ViewPdfModal = lazy(() => import('#client/components/ViewPdfModal'));
const ExitConfirmModal = lazy(
  () => import('#client/components/CheckDeposit/components/ExitConfirmModal')
);

function MonthStatement({ id, year, month, last4Digits }) {
  const { accessToken } = useAuthentication();
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { createCommonErrorToast } = useToastContext();
  const { openModal, closeModal } = useModalContext();
  const [loading, setLoading] = useState(false);

  const period = `${year}-${convertMonthToNumber(month)}`;

  // Note: generatePath is a function that will crash if params are empty,
  // so we need to make sure they are not empty before calling it
  const createEndpoint = () =>
    generatePath(`${DEFAULT_SEGMENT}/statements/:businessEntityUuid/:id/pdf?period=${period}`, {
      businessEntityUuid,
      id
    });

  const capitalizedMonth = month.charAt(0).toUpperCase() + month.slice(1);
  const documentTitle = `${capitalizedMonth} Business Checking (..${last4Digits})`;

  const viewPDF = async () => {
    // check if user is supported
    const isNotSupportedBrowser = checkNotAllowedBrowsersForPDF();
    if (isNotSupportedBrowser) {
      openModal({
        component: ExitConfirmModal,
        title: 'Browser update recommended.',
        description: 'For the best experience, please update your browser or try another one.',
        okText: 'Update',
        onOk: () => {
          window.open('https://www.dev.zenbusiness.com/d/unsupported-browsers');
        }
      });
      setLoading(false);
      return;
    }

    try {
      const { blob, fileName } = await fetchPDF({ endpoint: createEndpoint(), accessToken });

      // convert to pdf base64
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => {
        openModal({
          name: MODAL_NAMES.VIEW_PDF_MODAL,
          component: ViewPdfModal,
          link: reader.result,
          title: documentTitle,
          onSave: () => saveAs(blob, fileName),
          handleBack: () => closeModal(MODAL_NAMES.VIEW_PDF_MODAL)
        });
      };
    } catch (error) {
      createCommonErrorToast({ error });
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    try {
      const { blob, fileName } = await fetchPDF({ endpoint: createEndpoint(), accessToken });
      saveAs(blob, fileName);
    } catch (error) {
      createCommonErrorToast({ error });
      setLoading(false);
    }
  };

  const fetchAndHandlePDF = (callback) => async () => {
    setLoading(true);

    await callback();

    setLoading(false);
  };

  return (
    <Document
      disabled={loading}
      handleDownload={fetchAndHandlePDF(downloadPDF)}
      handleView={fetchAndHandlePDF(viewPDF)}
      loading={loading}
      text={documentTitle}
    />
  );
}

MonthStatement.propTypes = {
  id: PropTypes.string.isRequired,
  month: PropTypes.string.isRequired,
  year: PropTypes.string.isRequired,
  last4Digits: PropTypes.string.isRequired
};

export default memo(MonthStatement);
